import "@/styles/froala.css";
import "@/styles/project-cover.css";
import "@/styles/participant-header.css";
import "@/styles/places.css";

import { ReactElement, ReactNode, useEffect, useMemo } from "react";

import merge from "lodash/merge";

import { AppProps } from "next/app";

import NextNprogress from "nextjs-progressbar";

import { NextPage } from "next";

import NextHead from "next/head";

import { TranslationsProvider } from "@eo-locale/react";
import { Provider as ReduxProvider } from "react-redux";

import { Provider as ChakraAppProvider } from "@/storybook-components/provider/provider";
import {
  createSystemFromTheme,
  theme as chakraUITheme
} from "@/storybook-components/theme/theme";
import { wrapper } from "@/redux/store/store";

import { yupConfig } from "@/lib";

import { useAppSelector } from "@/redux/store/hooks";
import { getEhqTheme } from "@/helpers/theme";
import useLiquidTemplateStyles from "@/hooks/useliquidTemplateStyles";

import { EOLocales } from "@/helpers/locale";
import AuthGateModal from "@/components/guest-login";
import { clearGuestProfilePromptDismissedIfNoActiveGuestSession } from "@/helpers/guest-profile-prompt-session";

// Declare webpack nonce global for TypeScript
// @ts-ignore - Used by webpack internally
// eslint-disable-next-line no-unused-vars
declare let __webpack_nonce__: string | undefined;

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  yupConfig(); // configure yup with translations

  const getLayout = Component.getLayout ?? ((page) => page);

  const { theme } = useAppSelector((state) => state.theme);
  const { nonce } = useAppSelector((state) => state.adapter.adapter);

  const { homePageRevision } = useAppSelector(
    (state) => state.homePageRevision
  );
  const liquidTemplateStylesNode = useLiquidTemplateStyles();
  const { customTrackingCode } = useAppSelector(
    (state) => state.settings.settings
  );

  const { ehqTheme } = getEhqTheme(theme, homePageRevision);

  const mergedTheme = useMemo(
    () => merge({}, chakraUITheme, ehqTheme),
    [theme, homePageRevision]
  );

  const chakraSystem = useMemo(
    () => createSystemFromTheme(mergedTheme),
    [mergedTheme]
  );

  // Set webpack nonce for dynamically loaded chunks
  useEffect(() => {
    if (nonce) {
      // @ts-ignore - __webpack_nonce__ is a webpack global
      __webpack_nonce__ = nonce;
    }
  }, [nonce]);

  const { locale } = useAppSelector((state) => state.site.site);
  const isGuestParticipationEnabled = useAppSelector(
    (state) =>
      state.features.features.guestLogin?.enabled === true &&
      !state.settings.settings.disableSignup
  );
  const isLoggedIn = useAppSelector(
    (state) => !!state.user.registeredUser.isLoggedIn
  );
  const { profile } = useAppSelector((state) => state.user.registeredUser);

  useEffect(() => {
    clearGuestProfilePromptDismissedIfNoActiveGuestSession(isLoggedIn, profile);
  }, [isLoggedIn, profile?.guest]);

  return (
    <ChakraAppProvider nonce={nonce} value={chakraSystem}>
      <NextHead>
        {liquidTemplateStylesNode && (
          <style
            nonce={nonce}
            dangerouslySetInnerHTML={{ __html: liquidTemplateStylesNode }}
          />
        )}
      </NextHead>
      <NextNprogress nonce={nonce} showOnShallow={false} />
      <TranslationsProvider language={locale} locales={EOLocales}>
        {getLayout(<Component {...pageProps} />)}
        {isGuestParticipationEnabled ? <AuthGateModal /> : null}
      </TranslationsProvider>
      {customTrackingCode && (
        <div dangerouslySetInnerHTML={{ __html: customTrackingCode }} />
      )}
    </ChakraAppProvider>
  );
};

const WrappedApp = (props: AppPropsWithLayout) => {
  const { store, props: appProps } = wrapper.useWrappedStore(props);
  return (
    <ReduxProvider store={store}>
      <App {...appProps} />
    </ReduxProvider>
  );
};

export default WrappedApp;
